import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import posthog from 'posthog-js'
import { useAuthStore } from '@/stores/authStore'
import api from '@/services/api'
import { AlertModal, AlertType } from '@/components/AlertModal'
import { Skeleton } from '@/components/SkeletonLoader'
import { Plus, Edit, Trash2, Package, Search, Filter, RefreshCw, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ArrowUpDown, Image, Eye, EyeOff } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'

export default function ProductManagePage() {
  const { user } = useAuthStore()
  const vendorId = user?.vendorId
  const queryClient = useQueryClient()
  const location = useLocation()
  const navigate = useNavigate()

  // Mobile detection
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768)
  
  // Pagination state
  const [page, setPage] = useState(1)
  const [limit] = useState(10)
  
  // Filter state
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [sortBy, setSortBy] = useState('created_at')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery)
      setPage(1) // Reset to first page on search
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Get list of feeds for feed selection
  const { data: feedsData } = useQuery({
    queryKey: ['inventory-feeds', vendorId],
    queryFn: () => api.inventory.listFeeds({ vendorId: vendorId?.toString() }),
    enabled: !!vendorId,
  })

  const feeds = feedsData?.data?.data?.items || feedsData?.data?.items || []

  // Check if we need to navigate to add page from navigation state
  useEffect(() => {
    const state = location.state as { openAddModal?: boolean; feedId?: number; externalProductId?: string } | null
    if (state?.openAddModal) {
      navigate('/vendor/inventory/experiences/add', {
        state: {
          feedId: state.feedId,
          externalProductId: state.externalProductId
        },
        replace: true
      })
    }
  }, [location.state, navigate, feeds])

  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean
    type: AlertType
    title: string
    message: string
    onConfirm?: () => void
    confirmText?: string
    cancelText?: string
  }>({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
  })

  // Get vendor's default feed to get feedId
  const { data: feedData, isLoading: isLoadingFeed } = useQuery({
    queryKey: ['inventory-feed', vendorId],
    queryFn: () => api.inventory.getFeed(undefined, vendorId?.toString()),
    enabled: !!vendorId,
  })

  const feed = feedData?.data?.data

  // Get paginated products
  const { data: productsData, isLoading: isLoadingProducts } = useQuery({
    queryKey: ['inventory-products', feed?.feedId, page, limit, debouncedSearch, statusFilter, sortBy, sortOrder],
    queryFn: () => api.inventory.listProducts(feed?.feedId?.toString() || '', {
      page,
      limit,
      search: debouncedSearch || undefined,
      status: statusFilter || undefined,
      sortBy,
      sortOrder,
    }),
    enabled: !!feed?.feedId,
  })

  const products = productsData?.data?.data?.items || []
  const pagination = productsData?.data?.data?.pagination || { page: 1, limit: 20, total: 0, totalPages: 1 }

  // Delete product mutation
  const deleteProductMutation = useMutation({
    mutationFn: async (productId: string) => {
      if (!feed?.feedId) {
        throw new Error('No feed found.')
      }
      // Get all products and remove the one being deleted
      const allProductsResponse = await api.inventory.listProducts(feed.feedId.toString(), { limit: 1000 })
      const allProducts = allProductsResponse?.data?.data?.items || []
      const updatedProducts = allProducts.filter((p: any) => p.product_id?.toString() !== productId)

      return api.inventory.updateFeed(feed.feedId.toString(), {
        products: updatedProducts
      })
    },
    onSuccess: (_data, productId) => {
      posthog.capture('experience_deleted', { product_id: productId, vendor_id: vendorId })
      queryClient.invalidateQueries({ queryKey: ['inventory-products'] })
      queryClient.invalidateQueries({ queryKey: ['inventory-feed', vendorId] })
      setAlertModal({
        isOpen: true,
        type: 'success',
        title: 'Success',
        message: 'Experience deleted successfully!',
      })
    },
    onError: (error: any) => {
      setAlertModal({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: error.response?.data?.error?.message || 'Failed to delete experience',
      })
    },
  })

  // Activate / deactivate an experience
  const setStatusMutation = useMutation({
    mutationFn: ({ productId, status }: { productId: string; status: 'active' | 'inactive' }) =>
      api.inventory.setProductStatus(productId, status),
    onSuccess: (_data, variables) => {
      posthog.capture('experience_status_changed', {
        product_id: variables.productId,
        status: variables.status,
        vendor_id: vendorId,
      })
      queryClient.invalidateQueries({ queryKey: ['inventory-products'] })
    },
    onError: (error: any) => {
      setAlertModal({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: error?.response?.data?.error?.message || error?.response?.data?.message || 'Failed to update experience status',
      })
    },
  })

  const handleToggleStatus = (product: any) => {
    const productId = product.product_id?.toString()
    if (!productId) return
    const nextStatus: 'active' | 'inactive' = product.status === 'active' ? 'inactive' : 'active'
    const title = product.content?.title || product.product_id_external || 'this experience'
    if (nextStatus === 'inactive') {
      setAlertModal({
        isOpen: true,
        type: 'warning',
        title: 'Deactivate Experience',
        message: `Deactivate "${title}"? It will be hidden from your storefront and from AI agents until you reactivate it.`,
        confirmText: 'Deactivate',
        cancelText: 'Cancel',
        onConfirm: () => setStatusMutation.mutate({ productId, status: nextStatus }),
      })
    } else {
      setStatusMutation.mutate({ productId, status: nextStatus })
    }
  }

  const handleDelete = (product: any) => {
    const externalId = product.product_id_external || product.external_product_id || product.product_id
    setAlertModal({
      isOpen: true,
      type: 'warning',
      title: 'Delete Experience',
      message: `Are you sure you want to delete "${product.content?.title || externalId}"? This action cannot be undone.`,
      onConfirm: () => {
        deleteProductMutation.mutate(product.product_id?.toString() || externalId)
      },
      confirmText: 'Delete',
      cancelText: 'Cancel',
    })
  }

  // Sync experiences mutation
  const syncExperiencesMutation = useMutation({
    mutationFn: async () => {
      if (!feed?.feedId) {
        throw new Error('No feed found')
      }
      return api.inventory.syncExperiences(feed.feedId.toString(), { vendorId: vendorId ? parseInt(vendorId.toString()) : undefined })
    },
    onSuccess: (response) => {
      const data = response.data?.data || response.data
      posthog.capture('experience_synced', { platform: feed?.platform, vendor_id: vendorId })
      queryClient.invalidateQueries({ queryKey: ['inventory-products'] })
      queryClient.invalidateQueries({ queryKey: ['inventory-feed', vendorId] })
      setAlertModal({
        isOpen: true,
        type: 'success',
        title: 'Success',
        message: data?.message || `Successfully synced experiences from ${feed?.platform}`,
        confirmText: 'OK',
      })
    },
    onError: (error: any) => {
      setAlertModal({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: error?.response?.data?.error?.message || error?.response?.data?.message || error?.message || 'Failed to sync experiences',
        confirmText: 'OK',
      })
    },
  })

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(column)
      setSortOrder('desc')
    }
  }

  // Strip HTML tags helper
  const stripHtml = (html: string) => {
    if (!html) return ''
    return html.replace(/<[^>]*>/g, '').substring(0, 100)
  }

  if (isLoadingFeed) {
    return (
      <div className="container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <Skeleton height="2rem" width="300px" style={{ borderRadius: '4px' }} />
          <Skeleton height="2.5rem" width="140px" style={{ borderRadius: '4px' }} />
        </div>
        <Skeleton height="400px" style={{ borderRadius: '8px' }} />
      </div>
    )
  }

  if (!feed) {
    return (
      <div className="container">
        <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
          <Package size={48} style={{ margin: '0 auto 1rem', color: 'var(--color-gray-400)' }} />
          <h3 style={{ marginBottom: '0.5rem' }}>No Inventory Feed</h3>
          <p style={{ color: 'var(--color-gray-600)', marginBottom: '1.5rem' }}>
            Create or connect an inventory feed to start managing your experiences.
          </p>
          <button
            className="button button-primary"
            onClick={() => navigate('/vendor/inventory')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <Plus size={18} />
            Go to Feeds
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="container">
      {/* Header */}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'flex-start' : 'center',
        marginBottom: '1.5rem',
        gap: isMobile ? '1rem' : '0'
      }}>
        <div>
          <h1 style={{ marginBottom: '0.5rem', fontSize: isMobile ? '1.5rem' : '2rem' }}>Manage Experiences</h1>
          <p style={{ color: 'var(--color-gray-600)', fontSize: '0.9rem' }}>
            Manage your experience catalog ({pagination.total} {pagination.total === 1 ? 'experience' : 'experiences'})
            {feed?.platform && feed.platform !== 'tixxly' && (
              <span style={{ marginLeft: '0.5rem', textTransform: 'capitalize' }}>• {feed.platform}</span>
            )}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', flexDirection: isMobile ? 'column' : 'row', width: isMobile ? '100%' : 'auto' }}>
          {feed?.platform && feed.platform !== 'tixxly' && (
            <button
              className="button"
              onClick={() => syncExperiencesMutation.mutate()}
              disabled={syncExperiencesMutation.isPending}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                width: isMobile ? '100%' : 'auto',
                border: '1px solid var(--color-gray-300)',
                backgroundColor: 'white',
                color: 'var(--color-gray-700)',
                opacity: syncExperiencesMutation.isPending ? 0.6 : 1,
                cursor: syncExperiencesMutation.isPending ? 'not-allowed' : 'pointer',
              }}
            >
              <RefreshCw size={18} style={{ animation: syncExperiencesMutation.isPending ? 'spin 1s linear infinite' : 'none' }} />
              {syncExperiencesMutation.isPending ? 'Syncing...' : 'Sync Experiences'}
            </button>
          )}
          {/* Add Experience removed — products are synced from Bokun */}
        </div>
      </div>

      {/* Search and Filter */}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <Search size={18} style={{
            position: 'absolute',
            left: '0.75rem',
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--color-gray-400)'
          }} />
          <input
            type="text"
            placeholder="Search experiences..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input"
            style={{ paddingLeft: '2.5rem', width: '100%' }}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Filter size={18} style={{ color: 'var(--color-gray-400)' }} />
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="input"
            style={{ minWidth: '150px' }}
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="discontinued">Discontinued</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '1px solid var(--color-gray-200)',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      }}>
        {isLoadingProducts ? (
          <div style={{ padding: '2rem' }}>
            <Skeleton height="40px" style={{ marginBottom: '0.5rem' }} />
            <Skeleton height="60px" style={{ marginBottom: '0.5rem' }} />
            <Skeleton height="60px" style={{ marginBottom: '0.5rem' }} />
            <Skeleton height="60px" style={{ marginBottom: '0.5rem' }} />
            <Skeleton height="60px" />
          </div>
        ) : products.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <Package size={48} style={{ margin: '0 auto 1rem', color: 'var(--color-gray-400)' }} />
            <h3 style={{ marginBottom: '0.5rem' }}>
              {pagination.total === 0 ? 'No Experiences' : 'No Experiences Found'}
            </h3>
            <p style={{ color: 'var(--color-gray-600)', marginBottom: '1rem' }}>
              {pagination.total === 0
                ? 'Sync experiences from your feeds or add a new experience manually.'
                : 'Try adjusting your search or filter criteria.'}
            </p>
            {pagination.total === 0 && (
              <div style={{ maxWidth: '480px', margin: '0 auto 1.5rem', padding: '1rem', background: '#fffbeb', borderRadius: '8px', border: '1px solid #fbbf24', textAlign: 'left' }}>
                <p style={{ fontSize: 'var(--text-sm)', color: '#92400e', margin: '0 0 0.5rem', fontWeight: 600 }}>No products found from your provider?</p>
                <p style={{ fontSize: 'var(--text-xs)', color: '#92400e', margin: 0, lineHeight: 1.6 }}>
                  Verify that your products are fully set up, live, and included in your contracts with your provider. Most issues are on the provider setup or contract scope rather than on our side.
                </p>
              </div>
            )}
            {pagination.total === 0 && (
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                <button
                  className="button button-primary"
                  onClick={() => navigate('/vendor/inventory')}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                >
                  <RefreshCw size={18} />
                  Go to Feeds
                </button>
                <button
                  className="button"
                  onClick={() => navigate('/vendor/inventory/experiences/add')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    border: '1px solid var(--color-gray-300)',
                    backgroundColor: 'white',
                    color: 'var(--color-gray-700)',
                  }}
                >
                  <Plus size={18} />
                  Add Manually
                </button>
              </div>
            )}
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
                <thead>
                  <tr style={{ backgroundColor: 'var(--color-gray-50)', borderBottom: '1px solid var(--color-gray-200)' }}>
                    <th style={{ padding: '0.875rem 1rem', textAlign: 'left', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--color-gray-600)', letterSpacing: '0.05em' }}>
                      Image
                    </th>
                    <th 
                      onClick={() => handleSort('content')}
                      style={{ padding: '0.875rem 1rem', textAlign: 'left', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--color-gray-600)', letterSpacing: '0.05em', cursor: 'pointer', userSelect: 'none' }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        Experience
                        <ArrowUpDown size={14} style={{ opacity: sortBy === 'content' ? 1 : 0.4 }} />
                      </div>
                    </th>
                    <th style={{ padding: '0.875rem 1rem', textAlign: 'left', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--color-gray-600)', letterSpacing: '0.05em' }}>
                      Status
                    </th>
                    <th style={{ padding: '0.875rem 1rem', textAlign: 'left', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--color-gray-600)', letterSpacing: '0.05em' }}>
                      Price
                    </th>
                    <th 
                      onClick={() => handleSort('created_at')}
                      style={{ padding: '0.875rem 1rem', textAlign: 'left', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--color-gray-600)', letterSpacing: '0.05em', cursor: 'pointer', userSelect: 'none' }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        Created
                        <ArrowUpDown size={14} style={{ opacity: sortBy === 'created_at' ? 1 : 0.4 }} />
                      </div>
                    </th>
                    <th style={{ padding: '0.875rem 1rem', textAlign: 'right', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--color-gray-600)', letterSpacing: '0.05em' }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product: any, index: number) => {
                    const imageUrl = product.media?.[0]?.url || product.content?.media?.[0]?.url
                    const price = product.offers?.[0]?.price
                    const currency = product.offers?.[0]?.currency || 'USD'
                    
                    return (
                      <tr 
                        key={product.product_id || `product-${index}`}
                        style={{ 
                          borderBottom: index < products.length - 1 ? '1px solid var(--color-gray-100)' : 'none',
                          transition: 'background-color 0.15s ease',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-gray-50)'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        {/* Image */}
                        <td style={{ padding: '0.75rem 1rem' }}>
                          <div style={{
                            width: '56px',
                            height: '56px',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            backgroundColor: 'var(--color-gray-100)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}>
                            {imageUrl ? (
                              <img
                                src={imageUrl}
                                alt={product.content?.title || 'Experience'}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none'
                                  e.currentTarget.parentElement!.innerHTML = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><polyline points="21 15 16 10 5 21"></polyline></svg>'
                                }}
                              />
                            ) : (
                              <Image size={24} style={{ color: 'var(--color-gray-400)' }} />
                            )}
                          </div>
                        </td>

                        {/* Experience Info */}
                        <td style={{ padding: '0.75rem 1rem', maxWidth: '400px' }}>
                          <div style={{ fontWeight: 500, color: 'var(--color-gray-900)', marginBottom: '0.25rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {product.content?.title || product.product_id_external || 'Untitled'}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--color-gray-500)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {stripHtml(product.content?.description) || 'No description'}
                          </div>
                          <div style={{ fontSize: '0.7rem', color: 'var(--color-gray-400)', marginTop: '0.25rem' }}>
                            ID: {product.product_id_external || product.product_id}
                          </div>
                        </td>

                        {/* Status */}
                        <td style={{ padding: '0.75rem 1rem' }}>
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '0.25rem 0.75rem',
                            borderRadius: '9999px',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            textTransform: 'capitalize',
                            backgroundColor:
                              product.status === 'active' ? '#dcfce7' :
                              product.status === 'inactive' ? '#fef3c7' : '#e5e7eb',
                            color:
                              product.status === 'active' ? '#166534' :
                              product.status === 'inactive' ? '#92400e' : '#374151',
                          }}>
                            {product.status}
                          </span>
                        </td>

                        {/* Price */}
                        <td style={{ padding: '0.75rem 1rem' }}>
                          {price !== undefined ? (
                            <div style={{ fontWeight: 600, color: 'var(--color-gray-900)' }}>
                              {currency} {price.toFixed(2)}
                            </div>
                          ) : (
                            <span style={{ color: 'var(--color-gray-400)', fontSize: '0.875rem' }}>—</span>
                          )}
                        </td>

                        {/* Created */}
                        <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: 'var(--color-gray-600)' }}>
                          {product.created_at ? new Date(product.created_at).toLocaleDateString() : '—'}
                        </td>

                        {/* Actions */}
                        <td style={{ padding: '0.75rem 1rem' }}>
                          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                            {product.status !== 'discontinued' && (
                              <button
                                onClick={() => handleToggleStatus(product)}
                                disabled={setStatusMutation.isPending}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: '0.5rem',
                                  borderRadius: '6px',
                                  border: '1px solid var(--color-gray-300)',
                                  backgroundColor: product.status === 'active' ? '#fff7ed' : '#ecfdf5',
                                  color: product.status === 'active' ? '#b45309' : '#047857',
                                  cursor: setStatusMutation.isPending ? 'not-allowed' : 'pointer',
                                  opacity: setStatusMutation.isPending ? 0.6 : 1,
                                  transition: 'all 0.15s ease',
                                }}
                                title={product.status === 'active' ? 'Deactivate (hide from storefront & AI)' : 'Activate (show on storefront & AI)'}
                              >
                                {product.status === 'active' ? <EyeOff size={16} /> : <Eye size={16} />}
                              </button>
                            )}
                            <button
                              onClick={() => {
                                const productId = product.product_id || product.product_id_external
                                navigate(`/vendor/inventory/experiences/edit/${productId}`, { state: { product } })
                              }}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '0.5rem',
                                borderRadius: '6px',
                                border: '1px solid var(--color-gray-300)',
                                backgroundColor: 'white',
                                color: 'var(--color-gray-700)',
                                cursor: 'pointer',
                                transition: 'all 0.15s ease',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = 'var(--color-gray-50)'
                                e.currentTarget.style.borderColor = 'var(--color-sky-blue)'
                                e.currentTarget.style.color = 'var(--color-sky-blue)'
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'white'
                                e.currentTarget.style.borderColor = 'var(--color-gray-300)'
                                e.currentTarget.style.color = 'var(--color-gray-700)'
                              }}
                              title="Edit"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              onClick={() => handleDelete(product)}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '0.5rem',
                                borderRadius: '6px',
                                border: '1px solid #fecaca',
                                backgroundColor: '#fef2f2',
                                color: '#dc2626',
                                cursor: 'pointer',
                                transition: 'all 0.15s ease',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#fee2e2'
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = '#fef2f2'
                              }}
                              title="Delete"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '1rem',
                borderTop: '1px solid var(--color-gray-200)',
                backgroundColor: 'var(--color-gray-50)',
                flexWrap: 'wrap',
                gap: '1rem',
              }}>
                <div style={{ fontSize: '0.875rem', color: 'var(--color-gray-600)' }}>
                  Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, pagination.total)} of {pagination.total} experiences
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <button
                    onClick={() => setPage(1)}
                    disabled={page === 1}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '0.5rem',
                      borderRadius: '6px',
                      border: '1px solid var(--color-gray-300)',
                      backgroundColor: 'white',
                      color: 'var(--color-gray-700)',
                      cursor: page === 1 ? 'not-allowed' : 'pointer',
                      opacity: page === 1 ? 0.5 : 1,
                    }}
                    title="First page"
                  >
                    <ChevronsLeft size={18} />
                  </button>
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '0.5rem',
                      borderRadius: '6px',
                      border: '1px solid var(--color-gray-300)',
                      backgroundColor: 'white',
                      color: 'var(--color-gray-700)',
                      cursor: page === 1 ? 'not-allowed' : 'pointer',
                      opacity: page === 1 ? 0.5 : 1,
                    }}
                    title="Previous page"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                      let pageNum: number
                      if (pagination.totalPages <= 5) {
                        pageNum = i + 1
                      } else if (page <= 3) {
                        pageNum = i + 1
                      } else if (page >= pagination.totalPages - 2) {
                        pageNum = pagination.totalPages - 4 + i
                      } else {
                        pageNum = page - 2 + i
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setPage(pageNum)}
                          style={{
                            minWidth: '36px',
                            padding: '0.5rem 0.75rem',
                            borderRadius: '6px',
                            border: pageNum === page ? '1px solid var(--color-sky-blue)' : '1px solid var(--color-gray-300)',
                            backgroundColor: pageNum === page ? 'var(--color-sky-blue)' : 'white',
                            color: pageNum === page ? 'white' : 'var(--color-gray-700)',
                            fontWeight: pageNum === page ? 600 : 400,
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                          }}
                        >
                          {pageNum}
                        </button>
                      )
                    })}
                  </div>
                  
                  <button
                    onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
                    disabled={page === pagination.totalPages}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '0.5rem',
                      borderRadius: '6px',
                      border: '1px solid var(--color-gray-300)',
                      backgroundColor: 'white',
                      color: 'var(--color-gray-700)',
                      cursor: page === pagination.totalPages ? 'not-allowed' : 'pointer',
                      opacity: page === pagination.totalPages ? 0.5 : 1,
                    }}
                    title="Next page"
                  >
                    <ChevronRight size={18} />
                  </button>
                  <button
                    onClick={() => setPage(pagination.totalPages)}
                    disabled={page === pagination.totalPages}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '0.5rem',
                      borderRadius: '6px',
                      border: '1px solid var(--color-gray-300)',
                      backgroundColor: 'white',
                      color: 'var(--color-gray-700)',
                      cursor: page === pagination.totalPages ? 'not-allowed' : 'pointer',
                      opacity: page === pagination.totalPages ? 0.5 : 1,
                    }}
                    title="Last page"
                  >
                    <ChevronsRight size={18} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        type={alertModal.type}
        title={alertModal.title}
        message={alertModal.message}
        onConfirm={alertModal.onConfirm}
        onClose={() => setAlertModal({
          isOpen: false,
          type: 'info',
          title: '',
          message: ''
        })}
        confirmText={alertModal.confirmText}
        cancelText={alertModal.cancelText}
      />
    </div>
  )
}
